import { ReactNode, Suspense } from 'react'
import { AuthProvider } from '@/features/auth/AuthProvider'
import { I18nProvider } from '@/i18n/I18nProvider'
import { GuestProvider } from '@/features/guest/GuestProvider'
import { ChallengeProvider } from '@/features/challenges/ChallengeProvider'
import { UserChallengeProvider } from '@/features/challenges/UserChallengeProvider'
import { UnifiedNotificationProvider } from '@/features/notifications/UnifiedNotificationProvider'
import { SimpleNotificationProvider } from '@/features/notifications/SimpleNotificationProvider'
import { AnalyticsProvider } from '@/services/analytics'
import { ShareProvider } from '@2cmstudio/share'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { LoadingScreen } from '@/components/LoadingScreen'

interface AppProvidersProps {
  children: ReactNode
}

// Group related providers to reduce nesting depth
const DataProviders = ({ children }: { children: ReactNode }) => (
  <ChallengeProvider>
    <UserChallengeProvider>
      {children}
    </UserChallengeProvider>
  </ChallengeProvider>
)

const CoreProviders = ({ children }: { children: ReactNode }) => (
  <AuthProvider>
    <GuestProvider>
      {children}
    </GuestProvider>
  </AuthProvider>
)

const UIProviders = ({ children }: { children: ReactNode }) => (
  <ShareProvider>
    <AnalyticsProvider>
      <SimpleNotificationProvider>
        <UnifiedNotificationProvider>
          {children}
        </UnifiedNotificationProvider>
      </SimpleNotificationProvider>
    </AnalyticsProvider>
  </ShareProvider>
)

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <ThemeProvider>
        <I18nProvider>
          <CoreProviders>
            <DataProviders>
              <UIProviders>
                {children}
              </UIProviders>
            </DataProviders>
          </CoreProviders>
        </I18nProvider>
      </ThemeProvider>
    </Suspense>
  )
}